// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Universe-Specific Exploration Configuration
 * 
 * Defines exploration locations themed for each universe.
 * Each universe has its own set of locations that match the universe's theme and lore.
 */

// ============================================================================
// MILKY WAY UNIVERSE - Standard Sci-Fi Locations
// ============================================================================
export const milkywayLocations = [
  // LOW RISK LOCATIONS
  {
    id: 'forest',
    name: 'Bioluminescent Forest',
    icon: '🌲',
    description: 'Dense vegetation with glowing flora',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 120, message: 'Harvested valuable bio-samples. +120 credits', icon: '🌿' },
      { type: 'positive', chance: 0.3, credits: 80, message: 'Discovered rare medicinal plants. +80 credits', icon: '🍃' },
      { type: 'negative', chance: 0.1, credits: -50, message: 'Mild toxic spores. Medical treatment. -50 credits', icon: '☠️' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Beautiful scenery. Crew morale boosted.', icon: '✨' }
    ]
  },
  {
    id: 'meadow',
    name: 'Crystal Meadow',
    icon: '🌸',
    description: 'Peaceful plains with crystalline formations',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.6, credits: 100, message: 'Collected decorative crystals. +100 credits', icon: '💎' },
      { type: 'positive', chance: 0.2, credits: 150, message: 'Found rare crystal specimens! +150 credits', icon: '✨' },
      { type: 'neutral', chance: 0.2, credits: 0, message: 'Serene landscape. Nothing of value found.', icon: '🌈' }
    ]
  },
  {
    id: 'stream',
    name: 'Mineral Stream',
    icon: '💧',
    description: 'Flowing water with mineral deposits',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 90, message: 'Extracted valuable minerals. +90 credits', icon: '⚗️' },
      { type: 'positive', chance: 0.3, credits: 130, message: 'Discovered rare earth elements. +130 credits', icon: '🔬' },
      { type: 'negative', chance: 0.1, credits: -40, message: 'Equipment water damage. -40 credits', icon: '💦' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Refreshing water source. Samples collected.', icon: '🌊' }
    ]
  },

  // MODERATE RISK LOCATIONS
  {
    id: 'cave',
    name: 'Ancient Cave System',
    icon: '🕳️',
    description: 'Deep caverns with unusual energy readings',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 200, message: 'Discovered valuable crystals! +200 credits', icon: '💎' },
      { type: 'positive', chance: 0.3, credits: 100, message: 'Found abandoned cargo cache. +100 credits', icon: '📦' },
      { type: 'negative', chance: 0.2, credits: -150, message: 'Cave collapse! Emergency extraction. -150 credits', icon: '⚠️' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Interesting geological formations. No resources.', icon: '🪨' }
    ]
  },
  {
    id: 'ice',
    name: 'Frozen Wasteland',
    icon: '❄️',
    description: 'Sub-zero temperatures, possible resources',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 180, message: 'Found frozen resource deposits. +180 credits', icon: '🧊' },
      { type: 'positive', chance: 0.3, credits: 120, message: 'Discovered ice crystals with rare elements. +120 credits', icon: '💎' },
      { type: 'negative', chance: 0.2, credits: -120, message: 'Blizzard conditions. Extra power needed. -120 credits', icon: '🌨️' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Harsh but stable conditions. No findings.', icon: '🥶' }
    ]
  },

  // HIGH RISK LOCATIONS
  {
    id: 'ruins',
    name: 'Alien Ruins',
    icon: '🏛️',
    description: 'Remnants of an ancient civilization',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.2, credits: 300, message: 'Ancient artifacts recovered! +300 credits', icon: '⚡' },
      { type: 'positive', chance: 0.15, credits: 0, weapon: 'PHOTON_LANCE', message: 'Discovered ancient weapon technology!', icon: '⚔️' },
      { type: 'positive', chance: 0.15, credits: 150, message: 'Salvaged advanced technology. +150 credits', icon: '🔧' },
      { type: 'negative', chance: 0.3, credits: -200, message: 'Triggered defense systems! -200 credits', icon: '🚨' },
      { type: 'negative', chance: 0.2, credits: -100, message: 'Structural instability. Quick retreat. -100 credits', icon: '💥' }
    ]
  },

  // EXTREME RISK LOCATIONS
  {
    id: 'black_hole',
    name: 'Singularity Edge',
    icon: '⚫',
    description: 'Event horizon of a micro black hole',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.1, credits: 500, message: 'Harvested exotic matter! +500 credits', icon: '🌌' },
      { type: 'positive', chance: 0.1, credits: 0, weapon: 'SINGULARITY_TORPEDO', message: 'Found legendary weapon in the void!', icon: '⚔️' },
      { type: 'positive', chance: 0.2, credits: 350, message: 'Collected gravitational energy. +350 credits', icon: '⚡' },
      { type: 'negative', chance: 0.4, credits: -320, message: 'Gravitational shear! Critical damage. -320 credits', icon: '💥' },
      { type: 'negative', chance: 0.2, credits: -220, message: 'Time dilation effects. Systems desync. -220 credits', icon: '⏰' }
    ]
  }
];

// ============================================================================
// IMPROBABILITY SECTOR - Absurd Hitchhiker's Guide Locations
// ============================================================================
export const improbabilityLocations = [
  // LOW RISK LOCATIONS
  {
    id: 'towel_dispensary',
    name: 'Towel Dispensary',
    icon: '🏪',
    description: 'A mysteriously well-stocked towel distribution center',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.6, credits: 142, message: 'Found the perfect towel! Hoopy froods approve. +142 credits', icon: '🏖️' },
      { type: 'positive', chance: 0.3, credits: 84, message: 'Collected premium towels for trade. +84 credits', icon: '🧺' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Just regular towels. Still useful though.', icon: '🤷' }
    ]
  },
  {
    id: 'probability_garden',
    name: 'Probability Garden',
    icon: '🌺',
    description: 'Where flowers bloom based on statistical likelihood',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.42, credits: 123, message: 'Harvested improbable flowers! +123 credits', icon: '🌸' },
      { type: 'positive', chance: 0.42, credits: 87, message: 'Found statistically unlikely fruits. +87 credits', icon: '🍎' },
      { type: 'negative', chance: 0.08, credits: -42, message: 'Probability storm! Minor confusion. -42 credits', icon: '🌪️' },
      { type: 'neutral', chance: 0.08, credits: 0, message: 'Garden exists in quantum superposition. Schrödinger approves.', icon: '🐱' }
    ]
  },
  {
    id: 'tea_plantation',
    name: 'Infinite Tea Plantation',
    icon: '🍵',
    description: 'Where the universe\'s best tea grows in impossible quantities',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 96, message: 'Harvested premium tea leaves! +96 credits', icon: '🍃' },
      { type: 'positive', chance: 0.4, credits: 156, message: 'Found the legendary Earl Grey Supreme! +156 credits', icon: '👑' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Had a lovely cup of tea. Feeling refreshed.', icon: '☕' }
    ]
  },

  // MODERATE RISK LOCATIONS
  {
    id: 'logic_loop',
    name: 'Logic Loop',
    icon: '🔄',
    description: 'A circular argument that somehow generates energy',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 178, message: 'Harvested paradox energy! +178 credits', icon: '⚡' },
      { type: 'positive', chance: 0.3, credits: 134, message: 'Found logical inconsistencies worth selling. +134 credits', icon: '🤔' },
      { type: 'negative', chance: 0.2, credits: -89, message: 'Got stuck in recursive thinking. Lost time. -89 credits', icon: '🌀' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Logic is overrated anyway.', icon: '🤷' }
    ]
  },
  {
    id: 'babel_fish_pond',
    name: 'Babel Fish Pond',
    icon: '🐠',
    description: 'Natural habitat of universal translators',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 201, message: 'Caught rare Babel fish! +201 credits', icon: '🐟' },
      { type: 'positive', chance: 0.3, credits: 145, message: 'Found fish food that tastes like meaning. +145 credits', icon: '🍤' },
      { type: 'negative', chance: 0.2, credits: -67, message: 'Fish translated your thoughts. Embarrassing. -67 credits', icon: '😳' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Had a philosophical conversation with fish.', icon: '💭' }
    ]
  },

  // HIGH RISK LOCATIONS
  {
    id: 'restaurant_end_universe',
    name: 'Restaurant at the End of the Universe',
    icon: '🍽️',
    description: 'Fine dining with a view of universal destruction',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.2, credits: 342, message: 'Dined on the finest cuisine! +342 credits worth of experience', icon: '🥘' },
      { type: 'positive', chance: 0.15, credits: 0, weapon: 'QUANTUM_MISSILE', message: 'Found experimental weapon in the kitchen!', icon: '⚔️' },
      { type: 'positive', chance: 0.15, credits: 278, message: 'Collected rare spices from across time. +278 credits', icon: '🌶️' },
      { type: 'negative', chance: 0.3, credits: -189, message: 'Bill was astronomical! -189 credits', icon: '💸' },
      { type: 'negative', chance: 0.2, credits: -123, message: 'Witnessed universe ending. Existential crisis. -123 credits', icon: '😱' }
    ]
  },
  {
    id: 'vogon_poetry_reading',
    name: 'Vogon Poetry Reading',
    icon: '📖',
    description: 'The third worst poetry in the universe',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.2, credits: 234, message: 'Survived! Sold story to tabloids. +234 credits', icon: '📰' },
      { type: 'positive', chance: 0.2, credits: 167, message: 'Found earplugs market opportunity. +167 credits', icon: '🔌' },
      { type: 'negative', chance: 0.4, credits: -234, message: 'Poetry caused physical pain! Medical bills. -234 credits', icon: '🤕' },
      { type: 'negative', chance: 0.2, credits: -156, message: 'Ears still ringing. Therapy needed. -156 credits', icon: '👂' }
    ]
  },

  // EXTREME RISK LOCATIONS
  {
    id: 'total_perspective_vortex',
    name: 'Total Perspective Vortex',
    icon: '🌀',
    description: 'Shows you your true place in the universe',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.1, credits: 542, message: 'Achieved enlightenment! Cosmic wisdom is valuable. +542 credits', icon: '🧘' },
      { type: 'positive', chance: 0.2, credits: 398, message: 'Survived with ego intact! Rare achievement. +398 credits', icon: '💪' },
      { type: 'negative', chance: 0.5, credits: -345, message: 'Existential breakdown! Extensive therapy required. -345 credits', icon: '😵' },
      { type: 'negative', chance: 0.2, credits: -267, message: 'Realized cosmic insignificance. Depression treatment. -267 credits', icon: '😢' }
    ]
  },
  {
    id: 'infinite_improbability',
    name: 'Infinite Improbability Field',
    icon: '∞',
    description: 'Where anything can happen, and usually does',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.25, credits: 999, message: 'Won the cosmic lottery! +999 credits', icon: '🎰' },
      { type: 'positive', chance: 0.25, credits: 42, message: 'Found the Answer to Everything! +42 credits', icon: '🔢' },
      { type: 'negative', chance: 0.25, credits: -666, message: 'Turned into a penguin briefly. Dry cleaning. -666 credits', icon: '🐧' },
      { type: 'negative', chance: 0.25, credits: -123, message: 'Experienced impossible physics. Headache. -123 credits', icon: '🤯' }
    ]
  }
];

// ============================================================================
// DISCWORLD UNIVERSE - Magical Terry Pratchett Locations
// ============================================================================
export const discworldLocations = [
  // LOW RISK LOCATIONS
  {
    id: 'witch_cottage',
    name: 'Witch\'s Cottage',
    icon: '🏠',
    description: 'A cozy cottage with suspiciously smoking chimney',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 115, message: 'Traded for magical herbs and potions. +115 credits', icon: '🌿' },
      { type: 'positive', chance: 0.3, credits: 87, message: 'Received blessing of good fortune. +87 credits', icon: '✨' },
      { type: 'negative', chance: 0.1, credits: -45, message: 'Accidentally insulted the cat. Mild curse. -45 credits', icon: '🐱' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Had tea and biscuits. Lovely conversation.', icon: '🍪' }
    ]
  },
  {
    id: 'troll_bridge',
    name: 'Troll Bridge',
    icon: '🌉',
    description: 'A stone bridge with a surprisingly philosophical troll',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.6, credits: 98, message: 'Paid toll, got directions to treasure. +98 credits', icon: '💰' },
      { type: 'positive', chance: 0.2, credits: 134, message: 'Troll shared ancient wisdom. Valuable insights! +134 credits', icon: '🧠' },
      { type: 'negative', chance: 0.1, credits: -67, message: 'Failed riddle. Had to pay extra toll. -67 credits', icon: '❓' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Discussed the meaning of life with troll.', icon: '💭' }
    ]
  },
  {
    id: 'cabbage_field',
    name: 'Cabbage Field',
    icon: '🥬',
    description: 'Suspiciously ordinary cabbages (probably)',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 76, message: 'Harvested premium cabbages. +76 credits', icon: '🥬' },
      { type: 'positive', chance: 0.3, credits: 123, message: 'Found rare purple cabbages! +123 credits', icon: '💜' },
      { type: 'negative', chance: 0.1, credits: -34, message: 'Cabbages were slightly carnivorous. -34 credits', icon: '🦷' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Just normal cabbages. Surprisingly disappointing.', icon: '😐' }
    ]
  },

  // MODERATE RISK LOCATIONS
  {
    id: 'unseen_university_library',
    name: 'L-Space Library',
    icon: '📚',
    description: 'Where all knowledge connects through L-Space',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 189, message: 'Found valuable rare books! +189 credits', icon: '📖' },
      { type: 'positive', chance: 0.3, credits: 145, message: 'Librarian helped find useful information. +145 credits', icon: '🦍' },
      { type: 'negative', chance: 0.2, credits: -89, message: 'Got lost in L-Space. Rescue mission needed. -89 credits', icon: '🌀' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Learned that knowledge is power. Ook!', icon: '🍌' }
    ]
  },
  {
    id: 'dragon_lair',
    name: 'Dragon\'s Lair',
    icon: '🐉',
    description: 'Home of a noble dragon (probably not hungry)',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.35, credits: 234, message: 'Dragon shared part of hoard! +234 credits', icon: '💎' },
      { type: 'positive', chance: 0.35, credits: 167, message: 'Collected shed dragon scales. +167 credits', icon: '🛡️' },
      { type: 'negative', chance: 0.25, credits: -134, message: 'Dragon was having a bad day. Singed equipment. -134 credits', icon: '🔥' },
      { type: 'neutral', chance: 0.05, credits: 0, message: 'Dragon was out. Left a polite note.', icon: '📝' }
    ]
  },

  // HIGH RISK LOCATIONS
  {
    id: 'dungeon_dimensions_rift',
    name: 'Dungeon Dimensions Rift',
    icon: '🕳️',
    description: 'A tear in reality leading to Things Man Was Not Meant To Know',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.25, credits: 345, message: 'Harvested eldritch energy! +345 credits', icon: '⚡' },
      { type: 'positive', chance: 0.25, credits: 278, message: 'Found artifacts from beyond reality. +278 credits', icon: '🔮' },
      { type: 'negative', chance: 0.35, credits: -234, message: 'Glimpsed Things. Therapy and memory modification. -234 credits', icon: '👁️' },
      { type: 'negative', chance: 0.15, credits: -167, message: 'Reality hiccup. Temporary existence issues. -167 credits', icon: '❓' }
    ]
  },
  {
    id: 'death_domain',
    name: 'Death\'s Domain',
    icon: '💀',
    description: 'The anthropomorphic personification\'s home',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.2, credits: 312, message: 'DEATH WAS SURPRISINGLY HELPFUL. +312 credits', icon: '⚰️' },
      { type: 'positive', chance: 0.15, credits: 0, weapon: 'PHASE_DISRUPTOR', message: 'Death gifted you a spectral weapon.', icon: '⚔️' },
      { type: 'positive', chance: 0.15, credits: 234, message: 'Traded with Death for rare items. +234 credits', icon: '🎭' },
      { type: 'negative', chance: 0.3, credits: -189, message: 'Accidentally challenged Death to a game. Lost. -189 credits', icon: '🎲' },
      { type: 'negative', chance: 0.2, credits: -145, message: 'Existential crisis from meeting Death. -145 credits', icon: '😰' }
    ]
  },

  // EXTREME RISK LOCATIONS
  {
    id: 'narrative_causality_storm',
    name: 'Narrative Causality Storm',
    icon: '📚',
    description: 'Where stories write themselves into reality',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.2, credits: 456, message: 'Became the hero of your own story! +456 credits', icon: '🦸' },
      { type: 'positive', chance: 0.2, credits: 389, message: 'Harvested pure narrative energy. +389 credits', icon: '✍️' },
      { type: 'negative', chance: 0.4, credits: -345, message: 'Got cast as comic relief. Dignity damage. -345 credits', icon: '🤡' },
      { type: 'negative', chance: 0.2, credits: -267, message: 'Story had unhappy ending. Plot armor failed. -267 credits', icon: '📖' }
    ]
  },
  {
    id: 'great_atuin_shell',
    name: 'Great A\'Tuin\'s Shell',
    icon: '🐢',
    description: 'The World Turtle\'s shell - foundation of everything',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.15, credits: 567, message: 'Found cosmic turtle wisdom! Priceless insights. +567 credits', icon: '🌌' },
      { type: 'positive', chance: 0.25, credits: 423, message: 'Collected shell fragments. Literally foundational. +423 credits', icon: '🛡️' },
      { type: 'negative', chance: 0.4, credits: -389, message: 'Turtle moved slightly. Earthquake damage. -389 credits', icon: '🌍' },
      { type: 'negative', chance: 0.2, credits: -234, message: 'Realized you\'re on a turtle. Vertigo treatment. -234 credits', icon: '😵' }
    ]
  }
];

// ============================================================================
// DUNGEON DIMENSIONS - Eldritch Horror Locations
// ============================================================================
export const dungeonDimensionsLocations = [
  // LOW RISK LOCATIONS (relatively speaking)
  {
    id: 'probability_leak',
    name: 'Probability Leak',
    icon: '💧',
    description: 'Where mathematical certainty drips away',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 134, message: 'Collected leaked probability. Strangely valuable. +134 credits', icon: '🎲' },
      { type: 'positive', chance: 0.3, credits: 89, message: 'Found certainty crystals. +89 credits', icon: '💎' },
      { type: 'negative', chance: 0.2, credits: -67, message: 'Became temporarily uncertain of existence. -67 credits', icon: '❓' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Maybe found something. Or didn\'t. Hard to tell.', icon: '🤷' }
    ]
  },
  {
    id: 'concept_graveyard',
    name: 'Concept Graveyard',
    icon: '⚰️',
    description: 'Where failed ideas come to rest',
    risk: 'LOW',
    outcomes: [
      { type: 'positive', chance: 0.5, credits: 112, message: 'Salvaged useful failed concepts. +112 credits', icon: '💡' },
      { type: 'positive', chance: 0.3, credits: 156, message: 'Found a nearly-good idea! +156 credits', icon: '✨' },
      { type: 'negative', chance: 0.1, credits: -45, message: 'Absorbed some bad ideas. Mild confusion. -45 credits', icon: '🤔' },
      { type: 'neutral', chance: 0.1, credits: 0, message: 'Paid respects to dead concepts.', icon: '🪦' }
    ]
  },

  // MODERATE RISK LOCATIONS
  {
    id: 'logic_breakdown',
    name: 'Logic Breakdown Zone',
    icon: '🔧',
    description: 'Where cause and effect have given up',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.35, credits: 178, message: 'Harvested illogical energy! +178 credits', icon: '⚡' },
      { type: 'positive', chance: 0.35, credits: 145, message: 'Found paradox fragments. +145 credits', icon: '🔄' },
      { type: 'negative', chance: 0.25, credits: -123, message: 'Got caught in causality loop. Time wasted. -123 credits', icon: '🌀' },
      { type: 'neutral', chance: 0.05, credits: 0, message: 'Effect preceded cause. Surprisingly normal here.', icon: '⏰' }
    ]
  },
  {
    id: 'mathematics_graveyard',
    name: 'Mathematics Graveyard',
    icon: '🔢',
    description: 'Where numbers go when they stop making sense',
    risk: 'MODERATE',
    outcomes: [
      { type: 'positive', chance: 0.4, credits: 167, message: 'Found valuable irrational numbers! +167 credits', icon: '∞' },
      { type: 'positive', chance: 0.3, credits: 134, message: 'Collected broken equations. +134 credits', icon: '➗' },
      { type: 'negative', chance: 0.2, credits: -89, message: 'Divided by zero. Reality glitched. -89 credits', icon: '💥' },
      { type: 'neutral', chance: 0.1, credits: 0, message: '2+2=purple here. Seems reasonable.', icon: '🟣' }
    ]
  },

  // HIGH RISK LOCATIONS
  {
    id: 'reality_fault',
    name: 'Reality Fault Line',
    icon: '⚡',
    description: 'Where existence has structural problems',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.25, credits: 289, message: 'Harvested reality fragments! +289 credits', icon: '🌌' },
      { type: 'positive', chance: 0.25, credits: 234, message: 'Found stable existence pockets. +234 credits', icon: '🛡️' },
      { type: 'negative', chance: 0.35, credits: -234, message: 'Fell through reality crack. Expensive rescue. -234 credits', icon: '🕳️' },
      { type: 'negative', chance: 0.15, credits: -167, message: 'Temporarily ceased to exist. Paperwork nightmare. -167 credits', icon: '📋' }
    ]
  },
  {
    id: 'unspeakable_presence',
    name: 'Unspeakable Presence',
    icon: '👁️',
    description: 'Something that definitely shouldn\'t be looked at',
    risk: 'HIGH',
    outcomes: [
      { type: 'positive', chance: 0.15, credits: 345, message: 'Didn\'t look directly. Collected ambient horror. +345 credits', icon: '😎' },
      { type: 'positive', chance: 0.15, credits: 0, weapon: 'GRAVITON_BEAM', message: 'Thing dropped an impossible weapon.', icon: '⚔️' },
      { type: 'positive', chance: 0.1, credits: 267, message: 'Traded with Thing. It was surprisingly polite. +267 credits', icon: '🤝' },
      { type: 'negative', chance: 0.4, credits: -289, message: 'Looked directly at it. Sanity treatment required. -289 credits', icon: '🤯' },
      { type: 'negative', chance: 0.2, credits: -178, message: 'Thing noticed you. Awkward. Therapy needed. -178 credits', icon: '😰' }
    ]
  },

  // EXTREME RISK LOCATIONS
  {
    id: 'dungeon_heart',
    name: 'The Dungeon Heart',
    icon: '💜',
    description: 'The pulsing core of the Dungeon Dimensions',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.15, credits: 567, message: 'Touched the Heart of Madness! Cosmic insights! +567 credits', icon: '🧠' },
      { type: 'positive', chance: 0.15, credits: 456, message: 'Harvested pure eldritch energy. +456 credits', icon: '⚡' },
      { type: 'negative', chance: 0.5, credits: -456, message: 'Heart showed you Truth. Extensive therapy needed. -456 credits', icon: '😵' },
      { type: 'negative', chance: 0.2, credits: -345, message: 'Became temporarily eldritch. Transformation costs. -345 credits', icon: '🐙' }
    ]
  },
  {
    id: 'things_threshold',
    name: 'Threshold of Things',
    icon: '🚪',
    description: 'The boundary between known and unknowable',
    risk: 'EXTREME',
    outcomes: [
      { type: 'positive', chance: 0.1, credits: 666, message: 'Crossed threshold and returned! Impossible achievement! +666 credits', icon: '🏆' },
      { type: 'positive', chance: 0.2, credits: 423, message: 'Glimpsed Things and survived. Rare wisdom. +423 credits', icon: '👁️' },
      { type: 'negative', chance: 0.5, credits: -567, message: 'Saw Things Man Was Not Meant To Know. Memory wipe. -567 credits', icon: '🧽' },
      { type: 'negative', chance: 0.2, credits: -389, message: 'Things saw you back. Existential crisis. -389 credits', icon: '😱' }
    ]
  }
];

// ============================================================================
// UNIVERSE-SPECIFIC EXPLORATION TITLES
// ============================================================================
export const universeExplorationTitles = {
  'milkyway': {
    icon: '🌌',
    title: 'PLANETARY EXPLORATION'
  },
  'improbability': {
    icon: '🎲',
    title: 'IMPROBABLE EXPLORATION'
  },
  'discworld': {
    icon: '🐢',
    title: 'DISCWORLD EXPLORATION'
  },
  'dungeon-dimensions': {
    icon: '👁️',
    title: 'DIMENSIONAL EXPLORATION'
  }
};

// ============================================================================
// UNIVERSE LOCATION MAPPING
// ============================================================================
export const universeLocations = {
  'milkyway': milkywayLocations,
  'improbability': improbabilityLocations,
  'discworld': discworldLocations,
  'dungeon-dimensions': dungeonDimensionsLocations
};

/**
 * Get universe-specific exploration title and icon
 * @param {string} universeId - Universe identifier
 * @returns {Object} Object with icon and title properties
 */
export const getUniverseExplorationTitle = (universeId) => {
  return universeExplorationTitles[universeId] || universeExplorationTitles['milkyway'];
};

/**
 * Get exploration locations for a specific universe and threat level
 * @param {string} universeId - Universe identifier
 * @param {string} threatLevel - Planet threat level
 * @returns {Array} Array of 6 exploration locations
 */
export const getUniverseLocationsForThreat = (universeId, threatLevel) => {
  const locations = universeLocations[universeId] || universeLocations['milkyway'];
  const threat = threatLevel.toUpperCase();
  
  // Define risk distribution based on planet threat
  const riskWeights = {
    'LOW': { 'LOW': 0.6, 'MODERATE': 0.3, 'HIGH': 0.1, 'EXTREME': 0 },
    'MODERATE': { 'LOW': 0.2, 'MODERATE': 0.4, 'HIGH': 0.3, 'EXTREME': 0.1 },
    'HIGH': { 'LOW': 0, 'MODERATE': 0.2, 'HIGH': 0.5, 'EXTREME': 0.3 },
    'UNKNOWN': { 'LOW': 0.25, 'MODERATE': 0.25, 'HIGH': 0.25, 'EXTREME': 0.25 }
  };

  const weights = riskWeights[threat] || riskWeights['MODERATE'];
  
  // Build weighted pool of locations
  const weightedPool = [];
  locations.forEach(location => {
    const weight = weights[location.risk] || 0;
    const count = Math.round(weight * 100); // Convert to integer count
    for (let i = 0; i < count; i++) {
      weightedPool.push(location);
    }
  });
  
  // Shuffle the pool
  for (let i = weightedPool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [weightedPool[i], weightedPool[j]] = [weightedPool[j], weightedPool[i]];
  }
  
  // Select 6 unique locations
  const selected = [];
  const usedIds = new Set();
  
  for (const location of weightedPool) {
    if (!usedIds.has(location.id)) {
      selected.push(location);
      usedIds.add(location.id);
      if (selected.length === 6) break;
    }
  }
  
  // If we don't have 6 unique locations, fill with random ones
  if (selected.length < 6) {
    const remaining = locations.filter(loc => !usedIds.has(loc.id));
    while (selected.length < 6 && remaining.length > 0) {
      const randomIndex = Math.floor(Math.random() * remaining.length);
      selected.push(remaining[randomIndex]);
      remaining.splice(randomIndex, 1);
    }
  }
  
  return selected;
};

const UniverseExplorationConfig = {
  universeLocations,
  universeExplorationTitles,
  getUniverseLocationsForThreat,
  getUniverseExplorationTitle,
  milkywayLocations,
  improbabilityLocations,
  discworldLocations,
  dungeonDimensionsLocations
};

export default UniverseExplorationConfig;